import { useEffect, useRef, type RefObject } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { ACCOUNT_PARAM } from '@/app/accountSearchParam';
import { queryKeys } from '@/api/queryKeys';
import type { NewMailEvent, NewMailPoll } from '@/api/types';
import { isDesktopApp } from '@/utils/desktop';
import { recordArrivals } from './arrivals';
import { messageNoticeTag } from './messageNotice';
import { createLeaderElection, LEADER_CHANNEL_NAME } from './leader';

/**
 * The interim in-tab notification poller (ADR 0021 §4.6).
 *
 * ## What it is, stated plainly
 *
 * Instant and background delivery need `IdleWatcher` and a live stream. Until
 * both are real for an account, the only way this system can learn that mail
 * arrived is to ask, so it asks: `GET /accounts/{id}/new-mail?after={watermark}`
 * on a cadence, from **one** window per browser — per mailbox. The shell runs
 * one of these for every mailbox (`AccountNotificationPoller`), not just the
 * active one: a mailbox without background notifications has no other way to
 * notify at all. (The server no longer holds the background push back while a
 * tab is open — that silenced the owner's phone whenever the desktop app was
 * running — so a mailbox with them is announced by the push, and this poller
 * stands aside in a browser the push reaches: `backgroundPush` below.)
 *
 * ## Three properties that are not negotiable
 *
 * 1. **It never receives or evaluates rules.** The response carries decisions,
 *    already made server-side, and content only for messages the evaluator said
 *    *notify* about. A muted sender's subject never reaches this file, which is
 *    only true while the client has no copy of the rules to run.
 * 2. **The watermark is opaque.** It is stored and handed back verbatim. Its
 *    shape is the server's — per-folder UIDVALIDITY and last-UID state — and a
 *    client that parsed it would break the first time the watched set changed.
 * 3. **The cadence comes from the server.** `poll_after_seconds` is the number
 *    the server asked for; the hidden-tab multiplier below is the one client
 *    policy, and it only ever makes the poll *less* frequent.
 *
 * Arrivals feed the "N new messages" pill, never a splice into the list: mail
 * arriving must not move rows under the cursor of someone mid-triage.
 */

/** Hidden windows poll five times less often (60 s visible → 5 min hidden). */
const HIDDEN_MULTIPLIER = 5;
/** Floor on the server's cadence, so a bad value cannot become a hot loop. */
const MIN_INTERVAL_MS = 15_000;
/** One toast per account per 5 s (doc 05 §12). */
const TOAST_THROTTLE_MS = 5_000;
/**
 * The same pictures the push worker uses (public/push-sw.js). Without the
 * badge, Android draws Chrome's logo in the status bar and notice header.
 */
const NOTICE_ICON = '/icons/icon-192.png';
const NOTICE_BADGE = '/icons/badge-96.png';

/**
 * How long to wait before the next poll.
 *
 * A hidden browser tab backs off, because the user has other ways to hear
 * about mail (Web Push, if they turned it on) and a background tab should not
 * cost them battery. The desktop app is the exception: hidden to its tray is
 * how it runs, Web Push does not exist in its webview, and this poll is its
 * only way to notify (ADR 0051). Backing off there made tray notifications
 * arrive up to five minutes late. `desktop` comes from a page-visible global
 * any script could set; the worst it can do is poll at the server's own
 * cadence, which the server rate-limits anyway, so it is safe to trust here.
 */
export function pollIntervalMs(seconds: number, visible: boolean, desktop: boolean): number {
  const base = Math.max(seconds * 1_000, MIN_INTERVAL_MS);
  return visible || desktop ? base : base * HIDDEN_MULTIPLIER;
}

function documentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/** True only when the user cannot already see the mail arrive. */
function shouldNotify(): boolean {
  if (typeof document === 'undefined' || typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;
  return document.visibilityState !== 'visible' || !document.hasFocus();
}

/**
 * Where a click on this mailbox's notification should land.
 *
 * The same URL the Web Push worker opens (public/push-sw.js): folder and
 * message ids only mean something inside one account, so the route names the
 * account too and the app switches to it before honouring the path
 * (`ACCOUNT_PARAM`, app/accountSearchParam.ts). Ids are encoded the way every
 * other mail link in the app encodes them — an IMAP folder name is allowed to
 * contain a slash.
 */
function messagePath(accountId: string, event: NewMailEvent): string {
  const path = `/mail/${encodeURIComponent(event.folderId)}/${encodeURIComponent(event.messageId)}`;
  return `${path}?${ACCOUNT_PARAM}=${encodeURIComponent(accountId)}`;
}

/** How many messages of one poll get a notice of their own (as the push: push-sw.js). */
export const NOTICES_PER_BATCH = 4;

/** One system notice: what it says, its tag and where its click goes. */
export interface MailNotice {
  title: string;
  body: string;
  tag: string;
  url: string;
  /** Only the newest of a batch makes a sound. */
  silent: boolean;
}

/**
 * The notices for one batch (ADR 0106): one per message for the newest
 * NOTICES_PER_BATCH, each under that message's own tag
 * (`messageNoticeTag`) and opening that message, then one "N more new
 * messages" under the mailbox's tag for the rest. The tags are the push
 * worker's, so a message heard of both ways is still one notice.
 * `events[0]` is the newest. Listed oldest first, the order to raise them in
 * so the newest lands on top.
 */
export function mailNotices(
  accountId: string,
  label: string | undefined,
  events: readonly NewMailEvent[],
): MailNotice[] {
  const shown = events.slice(0, NOTICES_PER_BATCH);
  const notices: MailNotice[] = shown
    .map((event, index) => {
      const who = event.from.name?.trim() || event.from.email;
      // A connected mailbox names itself: "from Dana" alone does not say which
      // of several inboxes to go and look in.
      return {
        title: label ? `New mail from ${who} · ${label}` : `New mail from ${who}`,
        body: event.subject || '(no subject)',
        tag: messageNoticeTag(accountId, event.messageId),
        url: messagePath(accountId, event),
        silent: index !== 0,
      };
    })
    .reverse();
  const more = events.length - shown.length;
  const [first] = events;
  if (more > 0 && first !== undefined) {
    notices.unshift({
      title: `${more} more new ${more === 1 ? 'message' : 'messages'}` + (label ? ` · ${label}` : ''),
      body: '',
      tag: `ols-mail-${accountId}`,
      url: `/mail/${encodeURIComponent(first.folderId)}?${ACCOUNT_PARAM}=${encodeURIComponent(accountId)}`,
      silent: true,
    });
  }
  return notices;
}

/**
 * Raises this batch's notices, and wires their clicks.
 *
 * The handler goes on each `Notification` instance rather than on a global
 * listener: `onclick` fires for *that* notice, so it already knows which
 * message to open. It does what the service worker's `notificationclick` does
 * for a background notice: focus the window, close the notification (a click
 * that leaves it on screen reads as a click that did nothing) and take the SPA
 * to the message.
 */
async function notifyOnce(
  accountId: string,
  label: string | undefined,
  events: readonly NewMailEvent[],
  navigate: RefObject<NavigateFunction>,
  serverPushes: boolean,
): Promise<void> {
  if (events.length === 0 || !shouldNotify()) return;
  // The server is already pushing this mailbox to this browser, and the push
  // got here first (it is sent the moment IDLE hears the mail; this poll runs
  // up to five minutes later in a hidden tab). A second notice for the same
  // message would only be noise.
  if (serverPushes && (await browserHoldsPushSubscription())) return;
  let registration: ServiceWorkerRegistration | undefined;
  for (const notice of mailNotices(accountId, label, events)) {
    const options = { body: notice.body, tag: notice.tag, icon: NOTICE_ICON, badge: NOTICE_BADGE, silent: notice.silent };
    try {
      const notification = new Notification(notice.title, options);
      notification.onclick = () => {
        window.focus();
        notification.close();
        navigate.current(notice.url);
      };
      continue;
    } catch {
      /* Android: see below. */
    }
    // Chrome on Android refuses `new Notification()` outright ("Illegal
    // constructor"): a page there may only raise a notice through its service
    // worker. The worker's `notificationclick` opens `data.url`, exactly as it
    // does for a push (public/push-sw.js).
    try {
      registration ??= await navigator.serviceWorker?.getRegistration();
      await registration?.showNotification(notice.title, { ...options, data: { url: notice.url } });
    } catch {
      /* A browser that refuses both is not worth crashing over. */
    }
  }
}

/** Whether this browser holds a Web Push subscription the server can reach. */
async function browserHoldsPushSubscription(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    return (await registration?.pushManager?.getSubscription()) != null;
  } catch {
    return false;
  }
}

export interface NotificationPollerOptions {
  accountId: string | undefined;
  /** The account's saved preference. `false` means no polling at all. */
  enabled: boolean;
  /**
   * Names the mailbox in the notification title. Set for connected mailboxes;
   * the primary's title stays "New mail from …".
   */
  label?: string | undefined;
}

export function useNotificationPoller({
  accountId,
  enabled,
  label,
}: NotificationPollerOptions): void {
  const api = useApi();
  const queryClient = useQueryClient();

  // `navigate` changes identity across renders, and this effect owns a running
  // poller: depending on it would tear the poller down and re-elect a leader on
  // every render. The ref keeps the click handler on the current one instead.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // `api` is the context value, which is the app-wide singleton (or a test's
  // injected one) and is stable for the life of the provider — so it is a
  // dependency rather than something smuggled past the effect in a ref.
  useEffect(() => {
    if (accountId === undefined || accountId === '' || !enabled) return;

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let leading = false;
    /** Opaque. Never parsed, never persisted: a fresh leader starts clean, and
     * the server answers a missing `after` with a watermark and no events, so a
     * handover cannot raise a backlog of toasts. */
    let watermark: string | undefined;
    let lastToastAt = 0;
    /**
     * One poll at a time. A tab coming back into view (or winning an
     * election) asks for a poll at once, and on a machine that has just woken
     * that is the same moment the sleeping timer fires: two overlapping polls
     * raced for the watermark and each scheduled its own next timer, so the
     * poller forked into two chains that never merged again.
     */
    let inFlight = false;

    const schedule = (seconds: number): void => {
      if (stopped || !leading) return;
      // Exactly one pending timer: a second chain is a second poller.
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(
        () => void poll(),
        pollIntervalMs(seconds, documentVisible(), isDesktopApp()),
      );
    };

    const poll = async (): Promise<void> => {
      if (stopped || !leading || inFlight) return;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      let answer: NewMailPoll;
      inFlight = true;
      try {
        answer = await api.notifications.newMail(accountId, watermark);
      } catch {
        inFlight = false;
        // A 401 has already raised the re-auth dialog through the transport; a
        // 503 is retried by backing off one ordinary interval rather than
        // hammering a datastore that is already down.
        schedule(60);
        return;
      }
      inFlight = false;
      if (stopped) return;

      watermark = answer.watermark;
      queryClient.setQueryData(queryKeys.account(accountId).newMailPoll(), answer);

      // Arrivals go to the pill, one entry per folder. The list does not move.
      const byFolder = new Map<string, string[]>();
      for (const event of answer.events) {
        const ids = byFolder.get(event.folderId) ?? [];
        ids.push(event.messageId);
        byFolder.set(event.folderId, ids);
      }
      for (const [folderId, ids] of byFolder) recordArrivals(accountId, folderId, ids);

      // Quiet hours: the arrival above still reaches the pill, but no system
      // notice is raised for it. The server decides what is quiet; the tab
      // only obeys, exactly as the push worker never receives such a push.
      const audible = answer.events.filter((event) => event.quiet !== true);
      if (audible.length > 0 && Date.now() - lastToastAt >= TOAST_THROTTLE_MS) {
        lastToastAt = Date.now();
        void notifyOnce(accountId, label, audible, navigateRef, answer.backgroundPush === true);
      }

      // The server switched notifications off, or a real stream landed. Either
      // way this poller has no job left; it stands down rather than asking
      // again forever.
      if (!answer.enabled || !answer.interim) {
        stopped = true;
        return;
      }
      schedule(answer.pollAfterSeconds);
    };

    // One election per mailbox. A window runs a poller for every mailbox, and
    // two elections on one channel name in the same window would outrank each
    // other: one mailbox would lead and the other would never poll at all.
    const election = createLeaderElection({
      channelName: `${LEADER_CHANNEL_NAME}:${accountId}`,
      onChange: (isLeader) => {
        leading = isLeader;
        if (isLeader) {
          void poll();
          return;
        }
        if (timer !== undefined) clearTimeout(timer);
        timer = undefined;
      },
    });

    // Visibility changes both the cadence and the ranking, so the election is
    // re-run: the window the user just looked at should be the one polling.
    const onVisibility = (): void => {
      election.refresh();
      if (leading && documentVisible()) {
        if (timer !== undefined) clearTimeout(timer);
        void poll();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer !== undefined) clearTimeout(timer);
      election.stop();
    };
  }, [api, accountId, enabled, label, queryClient]);
}
